// OkoshiSessionManager — Koe Okoshi (transcription) WebRTC session lifecycle.
//
// spec §2.3.2:
//   1. POST /api/session/transcribe → receive ephemeral "ek_..." secret.
//   2. Capture user mic via getUserMedia.
//   3. RTCPeerConnection + addTrack(mic) + createDataChannel("oai-events").
//   4. Exchange SDP with https://api.openai.com/v1/realtime/calls
//      (generic GA endpoint, Bearer <ephemeral_secret>).
//   5. No remote audio track. No response.* events. Consume only
//      conversation.item.input_audio_transcription.{delta,completed}.
//   6. stop() gives the server a small grace period to flush the trailing
//      segment before closing the peer connection.
//
// The public surface is shaped for direct use from a React component: the
// UI re-reads live/final transcripts from the manager on every
// onTranscriptChange callback.

import type {
  OkoshiTranscribeModel,
  TranscribeSessionResponse,
} from "../../lib/api-types";
import type { OkoshiServerEvent } from "./events";

const REALTIME_SDP_URL = "https://api.openai.com/v1/realtime/calls";

/** Grace period after stop() before we force-close the peer connection.
 *  Slightly longer for gpt-realtime-whisper since it has no server_vad so
 *  OpenAI's default segmentation may take longer. */
const GRACE_PERIOD_MS_DEFAULT = 2000;
const GRACE_PERIOD_MS_WHISPER = 3000;
/** Polling cadence while waiting for the last .completed. */
const SETTLE_POLL_MS = 100;
/** Hard ceiling = 2 × grace period — we never wait longer than this. */
const SETTLE_HARD_CEILING_FACTOR = 2;

export type OkoshiState =
  | "idle"
  | "starting"
  | "ready"
  | "recording"
  | "stopping"
  | "error"
  | "closed";

export interface OkoshiSegment {
  text: string;
  completedAt: number;
}

export interface OkoshiStopResult {
  finalTranscript: string;
  durationSec: number;
  charCount: number;
  segmentCount: number;
  /** True when a non-empty currentDelta had to be force-pushed on stop —
   *  signals to the UI that the tail is unconfirmed (Phase 8 observation). */
  hadPendingDelta: boolean;
}

export interface OkoshiSessionCallbacks {
  onStateChange?: (state: OkoshiState) => void;
  onTranscriptChange?: () => void;
  onSessionStarted?: () => void;
  onSessionEnded?: () => void;
  onError?: (message: string) => void;
}

export class OkoshiSessionManager {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private localStream: MediaStream | null = null;
  private micTrack: MediaStreamTrack | null = null;

  private state: OkoshiState = "idle";
  private currentModel: OkoshiTranscribeModel | null = null;

  private startedAt: number | null = null;
  private readonly segments: OkoshiSegment[] = [];
  private currentDelta = "";

  /** Monotonic counter of segments — used to detect "new .completed arrived"
   *  inside waitForSegmentSettle without racing Date.now() measurements. */
  private segmentCounter = 0;

  /** Cached stop result for idempotency. */
  private lastStopResult: OkoshiStopResult | null = null;
  /** start() in flight, set by stop() to short-circuit the pending connect. */
  private abortStart = false;

  constructor(private readonly cb: OkoshiSessionCallbacks = {}) {}

  // ── Getters exposed to UI ──────────────────────────────────
  getState(): OkoshiState {
    return this.state;
  }

  getStartedAt(): number | null {
    return this.startedAt;
  }

  getDurationSec(): number {
    if (this.startedAt == null) return 0;
    return (Date.now() - this.startedAt) / 1000;
  }

  getSegments(): ReadonlyArray<OkoshiSegment> {
    return this.segments;
  }

  getCurrentDelta(): string {
    return this.currentDelta;
  }

  getSegmentCount(): number {
    return this.segments.length;
  }

  /** Live view: confirmed segments followed by the not-yet-confirmed delta.
   *  Separator is a single newline so UI can style the delta tail loosely. */
  getLiveTranscript(): string {
    const confirmed = this.segments.map((s) => s.text).join("\n");
    if (!this.currentDelta) return confirmed;
    return confirmed ? `${confirmed}\n${this.currentDelta}` : this.currentDelta;
  }

  /** Persistence view: only confirmed segments, joined with double newline
   *  so paragraph separation reads naturally in Markdown. */
  getFinalTranscript(): string {
    return this.segments.map((s) => s.text).join("\n\n");
  }

  getCharCount(): number {
    return this.getFinalTranscript().length;
  }

  // ── Public lifecycle ───────────────────────────────────────
  async start(
    model: OkoshiTranscribeModel,
    audioInputId?: string | null,
  ): Promise<void> {
    if (this.state !== "idle" && this.state !== "closed") {
      throw new Error(`cannot start from state ${this.state}`);
    }
    this.resetForNewSession();
    this.currentModel = model;
    this.setState("starting");
    this.abortStart = false;

    try {
      // ── 1. Mint ephemeral secret via our Worker ──
      const tokenRes = await fetch("/api/session/transcribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ model }),
      });
      if (this.abortStart) return this.rollbackPartialStart();
      if (!tokenRes.ok) {
        const text = await tokenRes.text().catch(() => "");
        throw new Error(
          `mint ephemeral token failed: ${tokenRes.status} ${text.slice(0, 200)}`,
        );
      }
      const session = (await tokenRes.json()) as TranscribeSessionResponse;

      // ── 2. Capture microphone ──
      const audioConstraints: MediaTrackConstraints = audioInputId
        ? { deviceId: { exact: audioInputId } }
        : {};
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...audioConstraints,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      if (this.abortStart) return this.rollbackPartialStart();

      const track = this.localStream.getAudioTracks()[0];
      if (!track) throw new Error("no audio track from getUserMedia");
      this.micTrack = track;
      // Okoshi streams continuously — the track is live from the start.
      track.enabled = true;

      // ── 3. RTCPeerConnection + DataChannel + track ──
      this.pc = new RTCPeerConnection();

      this.pc.addEventListener("connectionstatechange", () => {
        const s = this.pc?.connectionState;
        if (
          (s === "failed" || s === "disconnected") &&
          this.state !== "stopping" &&
          this.state !== "closed"
        ) {
          this.setState("error");
          this.cb.onError?.(`peer connection ${s}`);
        }
      });

      this.pc.addTrack(track, this.localStream);

      this.dc = this.pc.createDataChannel("oai-events");
      this.dc.addEventListener("open", () => {
        if (this.state === "starting") {
          this.setState("ready");
        }
      });
      this.dc.addEventListener("message", (ev) => {
        this.handleDataChannelMessage(ev.data);
      });
      this.dc.addEventListener("error", (ev) => {
        console.error("[okoshi] datachannel error", ev);
      });

      // ── 4. SDP exchange ──
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      if (this.abortStart) return this.rollbackPartialStart();

      const sdpRes = await fetch(REALTIME_SDP_URL, {
        method: "POST",
        headers: {
          authorization: `Bearer ${session.clientSecret}`,
          "content-type": "application/sdp",
        },
        body: offer.sdp ?? "",
      });
      if (this.abortStart) return this.rollbackPartialStart();
      if (!sdpRes.ok) {
        const text = await sdpRes.text().catch(() => "");
        throw new Error(
          `SDP exchange failed: ${sdpRes.status} ${text.slice(0, 200)}`,
        );
      }
      const answerSdp = await sdpRes.text();
      await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      if (this.abortStart) return this.rollbackPartialStart();

      // ── 5. Recording begins ──
      this.startedAt = Date.now();
      this.setState("recording");
      this.cb.onSessionStarted?.();
    } catch (err) {
      this.setState("error");
      this.closeConnection();
      const msg = err instanceof Error ? err.message : String(err);
      this.cb.onError?.(msg);
      throw err;
    }
  }

  /** Gracefully end the session. Idempotent.
   *  During `starting`, signals cancellation and resolves with an empty result. */
  async stop(): Promise<OkoshiStopResult> {
    if (this.state === "closed" && this.lastStopResult) {
      return this.lastStopResult;
    }
    if (this.state === "stopping") {
      // Another stop() is already in flight — await its completion via a
      // small polling loop rather than racing on a shared promise.
      while (this.state === "stopping") {
        await new Promise((r) => setTimeout(r, 50));
      }
      return (
        this.lastStopResult ?? {
          finalTranscript: "",
          durationSec: 0,
          charCount: 0,
          segmentCount: 0,
          hadPendingDelta: false,
        }
      );
    }

    if (this.state === "starting") {
      // Cancel the in-flight connect. Resolve with an empty result.
      this.abortStart = true;
      this.setState("stopping");
      this.closeConnection();
      const result: OkoshiStopResult = {
        finalTranscript: "",
        durationSec: 0,
        charCount: 0,
        segmentCount: 0,
        hadPendingDelta: false,
      };
      this.lastStopResult = result;
      this.setState("closed");
      this.cb.onSessionEnded?.();
      return result;
    }

    if (this.state === "idle" || this.state === "error") {
      // Nothing to wind down besides connection cleanup.
      this.closeConnection();
      const result: OkoshiStopResult = {
        finalTranscript: this.getFinalTranscript(),
        durationSec: this.getDurationSec(),
        charCount: this.getCharCount(),
        segmentCount: this.segments.length,
        hadPendingDelta: this.currentDelta.length > 0,
      };
      this.lastStopResult = result;
      this.setState("closed");
      return result;
    }

    // Normal path: recording or ready.
    this.setState("stopping");

    // 1. Stop sending new audio so OpenAI can flush its pending buffer.
    if (this.micTrack) this.micTrack.enabled = false;

    // 2. Wait for trailing .completed.
    const grace =
      this.currentModel === "gpt-realtime-whisper"
        ? GRACE_PERIOD_MS_WHISPER
        : GRACE_PERIOD_MS_DEFAULT;
    await this.waitForSegmentSettle(grace);

    // 3. If currentDelta is still non-empty, force-push it as a final segment.
    //    Common path for gpt-realtime-whisper (no server_vad); also a
    //    backstop for the VAD models in case the close raced the flush.
    const hadPendingDelta = this.currentDelta.length > 0;
    if (hadPendingDelta) {
      this.segments.push({
        text: this.currentDelta,
        completedAt: Date.now(),
      });
      this.currentDelta = "";
      this.segmentCounter++;
      this.cb.onTranscriptChange?.();
    }

    // 4. Close the peer.
    this.closeConnection();

    const result: OkoshiStopResult = {
      finalTranscript: this.getFinalTranscript(),
      durationSec: this.getDurationSec(),
      charCount: this.getCharCount(),
      segmentCount: this.segments.length,
      hadPendingDelta,
    };
    this.lastStopResult = result;
    this.setState("closed");
    this.cb.onSessionEnded?.();
    return result;
  }

  // ── Internals ──────────────────────────────────────────────
  private resetForNewSession() {
    this.segments.length = 0;
    this.currentDelta = "";
    this.segmentCounter = 0;
    this.startedAt = null;
    this.lastStopResult = null;
  }

  private rollbackPartialStart(): void {
    this.closeConnection();
    this.setState("closed");
  }

  private closeConnection() {
    try {
      this.dc?.close();
    } catch {
      /* ignore */
    }
    this.dc = null;
    try {
      this.pc?.close();
    } catch {
      /* ignore */
    }
    this.pc = null;
    this.micTrack?.stop();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.micTrack = null;
  }

  private setState(next: OkoshiState) {
    if (this.state === next) return;
    this.state = next;
    this.cb.onStateChange?.(next);
  }

  private handleDataChannelMessage(raw: unknown) {
    if (typeof raw !== "string") return;
    let parsed: OkoshiServerEvent;
    try {
      parsed = JSON.parse(raw) as OkoshiServerEvent;
    } catch (err) {
      console.warn("[okoshi] non-JSON dc message", err);
      return;
    }

    switch (parsed.type) {
      case "conversation.item.input_audio_transcription.delta": {
        const delta = (parsed as { delta?: string }).delta ?? "";
        if (delta) {
          this.currentDelta += delta;
          this.cb.onTranscriptChange?.();
        }
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const text = (parsed as { transcript?: string }).transcript ?? "";
        // Prefer the server-sent final transcript over whatever delta we had;
        // they can drift (e.g. mid-word correction).
        if (text) {
          this.segments.push({ text, completedAt: Date.now() });
          this.segmentCounter++;
          this.currentDelta = "";
          this.cb.onTranscriptChange?.();
        }
        break;
      }
      case "error": {
        const err = (parsed as { error?: { message?: string } }).error;
        this.cb.onError?.(err?.message ?? "realtime error");
        break;
      }
      case "session.created":
      case "session.updated":
        // debug-friendly, no state change
        break;
      default:
        // Unknown event — ignore silently.
        break;
    }
  }

  /** Poll until we believe OpenAI has flushed its last .completed. Strategy:
   *  wait up to `graceMs` of quiet after the most recent segment arrival;
   *  never wait longer than `graceMs × ceiling`. */
  private async waitForSegmentSettle(graceMs: number): Promise<void> {
    const hardDeadline = Date.now() + graceMs * SETTLE_HARD_CEILING_FACTOR;
    let lastSeenCounter = this.segmentCounter;
    let lastChangeAt = Date.now();

    while (Date.now() < hardDeadline) {
      await new Promise((r) => setTimeout(r, SETTLE_POLL_MS));
      if (this.segmentCounter !== lastSeenCounter) {
        lastSeenCounter = this.segmentCounter;
        lastChangeAt = Date.now();
        continue;
      }
      if (Date.now() - lastChangeAt >= graceMs) {
        // Quiet for long enough — settle.
        return;
      }
    }
  }
}
